import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Package } from "lucide-react";
import { ContentApi } from "../../../shared/api/contentApi.js";
import {
    UiDialogClose,
    UiDialogContent,
    UiDialogDescription,
    UiDialogRoot,
    UiDialogTitle,
    UiDialogHeader,
    UiDialogBody,
    UiDialogFooter,
} from "../../../components/ui/ant/Dialog.jsx";
import {
    Form,
    Input,
    Select, 
    Button,
    App
} from 'antd';

import {DialogRow} from "../components/DialogRow"


export default function NewProjectDialog({ open, onClose, onCreated }) {
    const [systemName, setSystemName] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;

        setSystemName("");
        setName("");
        setDescription("");
        setError("");

        const ac = new AbortController();
        // setLoadingGroups(true);
        return () => ac.abort();
    }, [open]);

    const canSubmit = useMemo(
        () => systemName.trim().length > 0 && name.trim().length > 0 && !submitting,
        [systemName, name, submitting],
    );

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;

        setSubmitting(true);
        setError("");

        try {
            const created = await ContentApi.createProject({
                systemName: systemName.trim(),
                name: name.trim(),
                description: description.trim() || null,
            });
            onCreated?.(created);
            onClose?.();
        } catch (e) {
            setError(e?.message || "Не удалось создать предмет");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <App>
            <UiDialogRoot
                open={open}
                onOpenChange={(next) => {
                    if (!next) onClose?.();
                }}
            >
                <UiDialogContent size="sm">
                    <UiDialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
                                <Package size={20} />
                            </div>
                            <div>
                                <UiDialogTitle>Создать проект</UiDialogTitle>
                                <UiDialogDescription>
                                    Создание проекта и версии
                                </UiDialogDescription>
                            </div>
                        </div>
                        <UiDialogClose showIcon />
                    </UiDialogHeader>

                    <UiDialogBody>
                        {error && (
                            <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                                {error}
                            </div>
                        )}

                        <Form layout='vertical' align='stretch' flex="true" onSubmit={onSubmit}>
                            <Form.Item label="System Name *">
                                <Input
                                    className="input min-w-full"
                                    value={systemName}
                                    onChange={(e) => setSystemName(e.target.value)}
                                    placeholder="iron_ingot"
                                    required
                                />
                            </Form.Item>

                            <Form.Item label="Название *">
                                <Input
                                    className="input min-w-full"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Iron Ingot"
                                    required
                                />
                            </Form.Item>

                            <Form.Item label="Описание" name="description">
                                <Input.TextArea
                                    className="input min-w-full"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Описание для UI"
                                />
                            </Form.Item>
                        </Form>
                    </UiDialogBody>

                    <UiDialogFooter>
                        <Button
                            type="button"
                            className="btn btn-soft-accent"
                            onClick={onSubmit}
                            disabled={!canSubmit}
                        >
                            {submitting ? "Создаем..." : "Создать"}
                        </Button>
                        <Button type="button" className="btn btn-outline" onClick={onClose}>
                            Отмена
                        </Button>
                    </UiDialogFooter>
                </UiDialogContent>
            </UiDialogRoot>
        </App>
    );
}

NewProjectDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onCreated: PropTypes.func,
};
